/**
 * Pagination Component - Mobile Optimized
 * 
 * Core reusable pagination control - theme-agnostic
 * Provides page navigation with customizable styling
 * 
 * Mobile Optimizations:
 * - WCAG 2.1 AA compliant touch targets (min 44x44px)
 * - Responsive layout that adapts to mobile screens
 * - Fewer page numbers shown on mobile
 * - Touch-friendly button spacing
 * - Accessible with proper ARIA labels
 */

'use client';

export interface PaginationProps {
  /**
   * Current active page (1-based)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Number of page buttons to show (excluding first, last, and ellipsis)
   * @default 5
   */
  siblingCount?: number;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Labels for translations
   */
  labels?: {
    previous?: string;
    next?: string;
    first?: string;
    last?: string;
  };
  /**
   * Show first/last buttons
   */
  showFirstLast?: boolean;
}

/**
 * Generate page numbers array with ellipsis
 */
function generatePagination(currentPage: number, totalPages: number, siblingCount: number = 1): (number | 'ellipsis')[] {
  const totalNumbers = siblingCount * 2 + 3; // siblings + current + first + last
  const totalBlocks = totalNumbers + 2; // + 2 ellipsis

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + i + 1);
    return [1, 'ellipsis', ...rightRange];
  }

  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages];
  }

  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

/**
 * Core Pagination Component
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className = '',
  labels = {
    previous: 'Previous',
    next: 'Next',
    first: 'First',
    last: 'Last',
  },
  showFirstLast = false,
}: PaginationProps) {
  const pages = generatePagination(currentPage, totalPages, siblingCount);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav className={`flex items-center justify-center gap-1.5 md:gap-2 ${className}`} aria-label="Pagination">
      {/* First Button - Mobile Optimized */}
      {showFirstLast && (
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          type="button"
          className="min-h-[44px] px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg md:rounded-xl hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation transition-colors"
          aria-label={labels.first}
        >
          <span className="hidden sm:inline">{labels.first}</span>
          <span className="sm:hidden">«</span>
        </button>
      )}

      {/* Previous Button - Mobile Optimized */}
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        type="button"
        className="min-h-[44px] px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg md:rounded-xl hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex items-center gap-1 transition-colors"
        aria-label={labels.previous}
      >
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        <span className="hidden sm:inline">{labels.previous}</span>
      </button>

      {/* Page Numbers - Mobile Optimized */}
      {pages.map((page, index) => {
        if (page === 'ellipsis') {
          return (
            <span key={`ellipsis-${index}`} className="px-2 md:px-3 py-2 text-gray-700 text-xs md:text-sm" aria-hidden="true">
              ...
            </span>
          );
        }

        const isActive = page === currentPage;

        return (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            type="button"
            className={`
              min-h-[44px] min-w-[44px] px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-medium rounded-lg md:rounded-xl transition-colors touch-manipulation
              ${isActive
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100'
              }
            `}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`Page ${page}`}
          >
            {page}
          </button>
        );
      })}

      {/* Next Button - Mobile Optimized */}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        type="button"
        className="min-h-[44px] px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg md:rounded-xl hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex items-center gap-1 transition-colors"
        aria-label={labels.next}
      >
        <span className="hidden sm:inline">{labels.next}</span>
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Last Button - Mobile Optimized */}
      {showFirstLast && (
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          type="button"
          className="min-h-[44px] px-3 md:px-4 py-2.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg md:rounded-xl hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation transition-colors"
          aria-label={labels.last}
        >
          <span className="hidden sm:inline">{labels.last}</span>
          <span className="sm:hidden">»</span>
        </button>
      )}
    </nav>
  );
}

